/* ============================================================================
   ControlsPanel.tsx — panneau de contrôle gauche du one-pager Marthe v2.
   Toutes les mutations passent par dispatch() du store canvasStore.
   Style techno terminal sobre (3secgraphic) : mono, border 1px stone-light,
   pas d'ombre, pas de gradient, pas d'icônes SVG packs.
   ============================================================================ */

import { useCallback } from 'react';
import {
  useCanvasParams,
  type GlyphSetId,
  type PaletteId,
  type BgId,
  type OrnamentStyleId,
  type SymmetryId,
  type CanvasMode,
  type PresenceVisualMode,
  type FairyQualityId,
} from '../../lib/canvasStore';
import { Section } from './Section';
import { Slider } from './Slider';
import { Toggle } from './Toggle';
import { RadioGroup, type RadioOption } from './RadioGroup';
import { FileDrop } from './FileDrop';
import { MartheGenerate } from './MartheGenerate';
import { GallerySection } from './GallerySection';
import './controls.css';

/* ----------------------------------------------------------------------------
   Static option lists — préférer constantes top-level (referential stability)
   ---------------------------------------------------------------------------- */

const MODE_OPTIONS: RadioOption<CanvasMode>[] = [
  { value: 'image-to-ascii', label: 'image' },
  { value: 'procedural', label: 'procedural' },
  { value: 'marthe', label: 'marthe' },
  { value: 'gallery', label: 'galerie' },
  { value: 'fluid', label: 'fluide' },
  { value: 'face', label: 'face' },
  { value: 'face2', label: 'présence' },
  { value: 'fucked-up', label: 'fucked up' },
  { value: 'fairy-world', label: 'monde des fées' },
];

const FAIRY_QUALITY_OPTIONS: RadioOption<FairyQualityId>[] = [
  { value: 'eco', label: 'eco' },
  { value: 'normal', label: 'normal' },
  { value: 'max', label: 'max' },
];

const GLYPH_SET_OPTIONS: RadioOption<GlyphSetId>[] = [
  { value: 'classic', label: 'classic', thumb: '@%#*+=' },
  { value: 'dense', label: 'dense', thumb: '█▓▒░ ·' },
  { value: 'minimal', label: 'minimal', thumb: '. : - +' },
  { value: 'fairy', label: 'fairy', thumb: '✦✧✶✷✵·' },
  { value: 'symbols', label: 'symbols', thumb: '◆◇○●□■' },
  { value: 'blocks', label: 'blocks', thumb: '▀▄█▌▐ ' },
  { value: 'cyanotype', label: 'cyanotype', thumb: '·°∘○●' },
  { value: 'halftone', label: 'halftone', thumb: '∘◦○⊙◉' },
  { value: 'liquid', label: 'liquid', thumb: '~≈∿∽' },
];

const PALETTE_OPTIONS: RadioOption<PaletteId>[] = [
  { value: 'mono-ink', label: 'mono', thumb: <Swatch shades={['#0d0d0d']} /> },
  { value: 'silver', label: 'silver', thumb: <Swatch shades={['#0d0d0d', '#8a8a8a', '#d8d4cc']} /> },
  { value: 'graphite', label: 'graphite', thumb: <Swatch shades={['#0d0d0d', '#2c2c2c', '#5a5a5a']} /> },
  { value: 'stone', label: 'stone', thumb: <Swatch shades={['#5a5a5a', '#8a8a8a', '#b8b8b8']} /> },
  { value: 'ash', label: 'ash', thumb: <Swatch shades={['#1a1a1a', '#5a5a5a', '#b8b8b8', '#f7f7f7']} /> },
];

const BG_OPTIONS: RadioOption<BgId>[] = [
  { value: 'paper', label: 'paper', thumb: <Swatch shades={['#ffffff']} /> },
  { value: 'ink', label: 'ink', thumb: <Swatch shades={['#0d0d0d']} /> },
];

const ORNAMENT_OPTIONS: RadioOption<OrnamentStyleId>[] = [
  { value: 'baroque-scroll', label: 'baroque', thumb: <OrnamentThumb kind="baroque-scroll" /> },
  { value: 'floral-wreath', label: 'floral', thumb: <OrnamentThumb kind="floral-wreath" /> },
  { value: 'vine-border', label: 'vine', thumb: <OrnamentThumb kind="vine-border" /> },
  { value: 'arabesque-mirror', label: 'arabesque', thumb: <OrnamentThumb kind="arabesque-mirror" /> },
];

const SYMMETRY_OPTIONS: RadioOption<SymmetryId>[] = [
  { value: 'horizontal', label: 'horizontal' },
  { value: 'vertical', label: 'vertical' },
  { value: 'asymmetric', label: 'asymmetric' },
];

const PRESENCE_MODE_OPTIONS: RadioOption<PresenceVisualMode>[] = [
  { value: 'isolated', label: 'isolated' },
  { value: 'overlay', label: 'overlay' },
];

/* ----------------------------------------------------------------------------
   ControlsPanel
   ---------------------------------------------------------------------------- */

export function ControlsPanel() {
  const [params, dispatch] = useCanvasParams();

  /* Helpers ---------------------------------------------------------------- */

  const setNum = useCallback(
    (key: keyof typeof params) => (value: number) =>
      dispatch({ type: 'set', key, value }),
    [dispatch],
  );

  const setBool = useCallback(
    (key: keyof typeof params) => (value: boolean) =>
      dispatch({ type: 'set', key, value }),
    [dispatch],
  );

  /* Image upload ----------------------------------------------------------- */

  const handleImageFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : null;
        if (dataUrl) {
          dispatch({ type: 'set', key: 'imageSrc', value: dataUrl });
        }
      };
      reader.readAsDataURL(file);
    },
    [dispatch],
  );

  const handleImageClear = useCallback(() => {
    dispatch({ type: 'set', key: 'imageSrc', value: null });
  }, [dispatch]);

  /* Seed ------------------------------------------------------------------- */

  const handleSeedCopy = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(params.seed.toString()).catch(() => {
      /* ignore — pas critique */
    });
  }, [params.seed]);

  /* Render ----------------------------------------------------------------- */

  return (
    <div
      className="controls-panel"
      data-testid="placeholder-controls"
      aria-label="Panneau de contrôle"
    >
      {/* === MODE ============================================================ */}
      <Section title="mode">
        <RadioGroup
          options={MODE_OPTIONS}
          value={params.mode}
          onChange={(v) => dispatch({ type: 'set', key: 'mode', value: v })}
        />
      </Section>

      {/* === IMAGE (mode = image-to-ascii) ================================== */}
      {params.mode === 'image-to-ascii' && (
        <Section title="image" hint="source">
          <FileDrop
            accept="image/*"
            currentSrc={params.imageSrc}
            onFile={handleImageFile}
            onClear={handleImageClear}
            hint="déposer image ou cliquer"
          />
          <Slider
            label="contrast"
            value={params.contrast}
            min={0.5}
            max={2.0}
            step={0.05}
            onChange={setNum('contrast')}
          />
          <Slider
            label="brightness"
            value={params.brightness}
            min={0.5}
            max={1.5}
            step={0.05}
            onChange={setNum('brightness')}
          />
          <Toggle
            label="invert"
            checked={params.invert}
            onChange={setBool('invert')}
          />
          <Slider
            label="dither"
            value={params.ditherAmount}
            min={0}
            max={1}
            step={0.05}
            onChange={setNum('ditherAmount')}
          />
        </Section>
      )}

      {/* === MARTHE SCENE (mode = marthe) =================================== */}
      {params.mode === 'marthe' && (
        <Section title="marthe scene" hint="prompt">
          <MartheGenerate />
        </Section>
      )}

      {/* === GALLERY (mode = gallery) ======================================= */}
      {params.mode === 'gallery' && <GallerySection />}

      {/* === PRESENCE (mode = face2) ======================================== */}
      {params.mode === 'face2' && (
        <Section title="presence" hint="vignettes">
          <div className="controls-sublabel">mode</div>
          <RadioGroup
            options={PRESENCE_MODE_OPTIONS}
            value={params.presenceMode}
            onChange={(v) =>
              dispatch({ type: 'set', key: 'presenceMode', value: v })
            }
          />
          <button
            type="button"
            className={`controls-action controls-action--inline${params.presenceFreeze ? ' controls-action--on' : ''}`}
            onClick={() =>
              dispatch({
                type: 'set',
                key: 'presenceFreeze',
                value: !params.presenceFreeze,
              })
            }
            data-testid="presence-freeze-button"
          >
            {params.presenceFreeze ? '▣ unfreeze' : '▢ freeze'}
          </button>
          <div className="controls-sublabel">styles (composables)</div>
          {(['surveillance', 'terminal', 'techno'] as const).map((styleKey) => {
            const active = params.presenceStyles[styleKey];
            return (
              <button
                key={styleKey}
                type="button"
                className={`controls-action controls-action--inline${active ? ' controls-action--on' : ''}`}
                onClick={() =>
                  dispatch({
                    type: 'set',
                    key: 'presenceStyles',
                    value: {
                      ...params.presenceStyles,
                      [styleKey]: !active,
                    },
                  })
                }
                data-testid={`presence-style-${styleKey}`}
              >
                {active ? '▣' : '▢'} {styleKey}
              </button>
            );
          })}
        </Section>
      )}

      {/* === MONDE DES FÉES (mode = fairy-world) =========================== */}
      {params.mode === 'fairy-world' && (
        <Section title="monde des fées" hint="explorer">
          <p className="marthe-generate__subtitle" style={{ margin: 0 }}>
            forêt de sapins nocturne en monde ouvert.
            <br />
            les lueurs dorées gardent les messages
            <br />
            laissés pour marthe — va les écouter.
          </p>
          <div className="controls-sublabel">qualité</div>
          <RadioGroup
            options={FAIRY_QUALITY_OPTIONS}
            value={params.fairyQuality}
            onChange={(v) =>
              dispatch({ type: 'set', key: 'fairyQuality', value: v })
            }
          />
          <div
            style={{
              marginTop: 'var(--sp-2)',
              fontSize: 11,
              lineHeight: 1.7,
              color: 'var(--stone)',
            }}
          >
            zqsd / wasd / flèches — marcher
            <br />
            shift — courir · espace — sauter
            <br />
            souris — regarder · échap — sortir
            <br />
            immobile 3s — les fées approchent
          </div>
        </Section>
      )}

      {/* === PROCEDURAL (mode = procedural) ================================= */}
      {params.mode === 'procedural' && (
        <Section title="procedural" hint="generative">
          <div className="controls-sublabel">ornement</div>
          <RadioGroup
            options={ORNAMENT_OPTIONS}
            value={params.ornamentStyle}
            onChange={(v) =>
              dispatch({ type: 'set', key: 'ornamentStyle', value: v })
            }
            stack
          />
          <div className="controls-sublabel">symmetry</div>
          <RadioGroup
            options={SYMMETRY_OPTIONS}
            value={params.symmetry}
            onChange={(v) =>
              dispatch({ type: 'set', key: 'symmetry', value: v })
            }
          />
          <div className="controls-row controls-row--seed">
            <span className="controls-label">seed</span>
            <button
              type="button"
              className="controls-seed"
              onClick={handleSeedCopy}
              title="cliquer pour copier dans le presse-papier"
              data-testid="seed-display"
            >
              {params.seed.toString().padStart(8, '0').slice(-8)}
            </button>
          </div>
          <button
            type="button"
            className="controls-action controls-action--inline"
            onClick={() => dispatch({ type: 'reseed' })}
            data-testid="reseed-button"
          >
            ↻ regenerate seed
          </button>
        </Section>
      )}

      {/* === RENDU =========================================================
          En mode fluide / face / face2 (présence) : on cache les contrôles
          ASCII-spécifiques (glyph set, palette, density/font/letter/line,
          scatter). On garde uniquement `background` car il sert au fluide
          (clearColor) et au mode présence overlay (glyph art derrière). */}
      {(() => {
        // Mode fairy-world : aucune option de rendu ASCII pertinente (scène
        // 3D autonome) → section masquée entièrement.
        if (params.mode === 'fairy-world') return null;
        const isVideoMode =
          params.mode === 'fluid' ||
          params.mode === 'face' ||
          params.mode === 'face2';
        return (
          <Section title="rendu" hint="render">
            {!isVideoMode && (
              <>
                <div className="controls-sublabel">glyph set</div>
                <RadioGroup
                  options={GLYPH_SET_OPTIONS}
                  value={params.glyphSet}
                  onChange={(v) =>
                    dispatch({ type: 'set', key: 'glyphSet', value: v })
                  }
                  stack
                />
                <div className="controls-sublabel">palette</div>
                <RadioGroup
                  options={PALETTE_OPTIONS}
                  value={params.palette}
                  onChange={(v) =>
                    dispatch({ type: 'set', key: 'palette', value: v })
                  }
                />
              </>
            )}
            <div className="controls-sublabel">background</div>
            <RadioGroup
              options={BG_OPTIONS}
              value={params.bgPalette}
              onChange={(v) =>
                dispatch({ type: 'set', key: 'bgPalette', value: v })
              }
            />
            {!isVideoMode && (
              <>
                <Slider
                  label="density"
                  value={params.density}
                  min={40}
                  max={200}
                  step={10}
                  onChange={setNum('density')}
                />
                <Slider
                  label="font size"
                  value={params.fontSize}
                  min={8}
                  max={20}
                  step={0.5}
                  onChange={setNum('fontSize')}
                />
                <Slider
                  label="letter spacing"
                  value={params.letterSpacing}
                  min={0}
                  max={4}
                  step={0.1}
                  onChange={setNum('letterSpacing')}
                />
                <Slider
                  label="line height"
                  value={params.lineHeight}
                  min={0.9}
                  max={1.6}
                  step={0.05}
                  onChange={setNum('lineHeight')}
                />
                <Slider
                  label="scatter"
                  value={params.scatterAmount}
                  min={0}
                  max={1}
                  step={0.02}
                  onChange={setNum('scatterAmount')}
                />
              </>
            )}
          </Section>
        );
      })()}

      {/* === HAND TRACKING (toggle moved to canvas top-right) =================
          Le bouton on/off est désormais au-dessus du canvas central.
          Cette section ne montre plus que le slider influence quand actif. */}
      {params.handTrackingEnabled && (
        <Section title="hand tracking" hint="influence">
          <Slider
            label="influence"
            value={params.handInfluence}
            min={0}
            max={1}
            step={0.05}
            onChange={setNum('handInfluence')}
          />
          <div className="controls-row controls-row--status">
            <span className="controls-status controls-status--on">● tracking</span>
          </div>
        </Section>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------------
   Atomes inline — Swatch + OrnamentThumb (reste local, pas de fichier dédié)
   ---------------------------------------------------------------------------- */

function Swatch({ shades }: { shades: string[] }) {
  return (
    <span className="controls-swatch" aria-hidden="true">
      {shades.map((c, i) => (
        <span key={i} style={{ background: c }} />
      ))}
    </span>
  );
}

interface OrnamentThumbProps {
  kind: OrnamentStyleId;
}

function OrnamentThumb({ kind }: OrnamentThumbProps) {
  // Mini SVG 24x24 stylisé — un trait par variante, pas de remplissage
  const stroke = 'currentColor';
  const sw = 1;
  switch (kind) {
    case 'baroque-scroll':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          aria-hidden="true"
        >
          <path d="M4 12 Q8 4 12 12 T20 12" />
          <circle cx="4" cy="12" r="1.2" />
          <circle cx="20" cy="12" r="1.2" />
        </svg>
      );
    case 'floral-wreath':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="6" r="1.5" />
          <circle cx="12" cy="18" r="1.5" />
          <circle cx="6" cy="12" r="1.5" />
          <circle cx="18" cy="12" r="1.5" />
        </svg>
      );
    case 'vine-border':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          aria-hidden="true"
        >
          <path d="M3 12 Q7 6 12 12 Q17 18 21 12" />
          <path d="M7 9 L7 11" />
          <path d="M17 13 L17 15" />
        </svg>
      );
    case 'arabesque-mirror':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          aria-hidden="true"
        >
          <path d="M12 3 L12 21" strokeDasharray="2 2" />
          <path d="M5 8 Q12 12 5 16" />
          <path d="M19 8 Q12 12 19 16" />
        </svg>
      );
  }
}
